import React from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { StatusBadge, ConfidenceBar } from '@/components/common/StatusBadge';
import { ClassificationBadge } from '@/components/opportunities/OppBadges';
import { Sparkles, Loader2, Mail, FileText, Image, MessageSquare, ClipboardList, Clock, FileSpreadsheet, PenLine } from 'lucide-react';
import { fmtDate, SOURCE_LABEL } from '@/lib/format';

const SRC_ICON = {
  email: Mail, drawing: PenLine, rfi: FileText, photo: Image,
  meeting_notes: ClipboardList, whatsapp: MessageSquare, daily_report: FileSpreadsheet, timesheet: Clock,
};

export const DetectionDrawer = ({ detection, onClose, onGenerate, generatingId }) => {
  const open = !!detection;
  const d = detection || {};
  return (
    <Sheet open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent className="w-full sm:max-w-lg p-0 flex flex-col" data-testid="detection-drawer">
        <SheetHeader className="px-6 py-4 border-b border-slate-200">
          <SheetTitle className="text-base">{d.title}</SheetTitle>
          <div className="flex items-center gap-2 pt-1">
            <StatusBadge status={d.status} />
            <span className="text-xs text-slate-500">{d.project_name} &middot; {d.trade}</span>
          </div>
        </SheetHeader>
        <ScrollArea className="flex-1">
          <div className="px-6 py-4 space-y-5">
            <div className="flex items-center justify-between rounded-lg bg-slate-50 border border-slate-200 px-4 py-3">
              <div>
                <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">Classification</div>
                <ClassificationBadge classification={d.classification} testid="detection-classification" />
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">Confidence</div>
                <ConfidenceBar value={d.confidence} />
              </div>
            </div>

            <Section title="What changed">{d.summary}</Section>
            <Section title="Contractual basis">{d.contractual_basis}</Section>

            <div>
              <div className="text-xs font-semibold text-slate-700 mb-2">Evidence trail ({(d.evidence || []).length})</div>
              <div className="space-y-2">
                {(d.evidence || []).map((e) => {
                  const Icon = SRC_ICON[e.source_type] || FileText;
                  return (
                    <div key={e.id} className="rounded-md border border-slate-200 bg-white p-3">
                      <div className="flex items-center gap-2">
                        <Icon className="h-4 w-4 text-slate-500" />
                        <span className="text-xs font-semibold text-slate-700">{SOURCE_LABEL[e.source_type] || e.source_type}</span>
                        <span className="text-[10px] text-slate-400 ml-auto">{fmtDate(e.timestamp)}</span>
                      </div>
                      <div className="text-xs font-medium text-slate-700 mt-1">{e.subject}</div>
                      <div className="text-xs text-slate-500 mt-0.5 line-clamp-3">{e.content}</div>
                      <div className="text-[10px] text-slate-400 mt-1">{e.sender}</div>
                    </div>
                  );
                })}
                {(d.evidence || []).length === 0 && <div className="text-xs text-slate-400">No evidence linked.</div>}
              </div>
            </div>
          </div>
        </ScrollArea>
        {d.status === 'new' && (
          <div className="px-6 py-4 border-t border-slate-200">
            <Button className="w-full bg-money hover:bg-money-700" disabled={generatingId === d.id}
              onClick={() => onGenerate(d)} data-testid="drawer-generate-co">
              {generatingId === d.id ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
              Generate Change Order Draft
            </Button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

const Section = ({ title, children }) => (
  <div>
    <div className="text-xs font-semibold text-slate-700 mb-1">{title}</div>
    <p className="text-sm text-slate-600 leading-relaxed">{children}</p>
  </div>
);
